import { useEffect, useState } from 'react';
import janetLogo from '@/assets/janet-marketing-logo.png';

interface LoadingScreenProps {
  onLoadingComplete?: () => void;
}

export function LoadingScreen({ onLoadingComplete }: LoadingScreenProps) {
  const [shouldFadeOut, setShouldFadeOut] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    // Prevent body scroll
    document.body.style.overflow = 'hidden';
    
    // Simulate loading progress
    const progressInterval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          clearInterval(progressInterval);
          return 100;
        }
        // Exponential progress for realistic feel
        return prev + (100 - prev) * 0.1;
      });
    }, 100);
    
    // Start fade-out after 3.5 seconds
    const fadeTimer = setTimeout(() => {
      setShouldFadeOut(true);
    }, 3500);

    // Complete loading and notify parent
    const completeTimer = setTimeout(() => {
      document.body.style.overflow = 'unset';
      onLoadingComplete?.();
    }, 4000); // 3500ms display + 500ms fade

    return () => {
      clearInterval(progressInterval);
      clearTimeout(fadeTimer);
      clearTimeout(completeTimer);
      document.body.style.overflow = 'unset';
    };
  }, [onLoadingComplete]);

  return (
    <div 
      className={`fixed inset-0 z-[9999] bg-gradient-to-br from-turquoise-very-dark via-[hsl(186,80%,10%)] to-turquoise-very-dark flex items-center justify-center transition-opacity duration-500 ${
        shouldFadeOut ? 'opacity-0' : 'opacity-100'
      }`}
      style={{ 
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100vw',
        height: '100vh',
      }}
    >
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-turquoise/10 rounded-full blur-3xl animate-float-slow" />
        <div className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-maroon/10 rounded-full blur-3xl animate-float-slower" />
      </div>

      <div className="relative flex flex-col items-center gap-8">
        {/* Logo with enhanced animation */}
        <div className="animate-logo-entrance">
          <img 
            src={janetLogo}
            alt="Janet Marketing Academy" 
            className="h-24 md:h-28 w-auto drop-shadow-2xl"
          />
        </div>

        {/* Enhanced Loading Spinner */}
        <div className="relative w-24 h-24">
          {/* Outer rotating ring - Maroon */}
          <div className="absolute inset-0">
            <svg className="w-full h-full animate-spin-slow" viewBox="0 0 100 100">
              <circle
                cx="50"
                cy="50"
                r="45"
                fill="none"
                stroke="hsl(0, 65%, 45%)"
                strokeWidth="3"
                strokeOpacity="0.2"
              />
              <circle
                cx="50"
                cy="50"
                r="45"
                fill="none"
                stroke="url(#maroonGradient)"
                strokeWidth="3"
                strokeDasharray="283"
                strokeDashoffset={283 - (283 * progress) / 100}
                strokeLinecap="round"
                className="transition-all duration-300"
                style={{ transformOrigin: 'center', transform: 'rotate(-90deg)' }}
              />
              <defs>
                <linearGradient id="maroonGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="hsl(0, 65%, 45%)" />
                  <stop offset="100%" stopColor="hsl(0, 65%, 35%)" />
                </linearGradient>
              </defs>
            </svg>
          </div>

          {/* Middle rotating ring - Turquoise */}
          <div className="absolute inset-3">
            <svg className="w-full h-full animate-spin-reverse" viewBox="0 0 100 100">
              <circle
                cx="50"
                cy="50"
                r="45"
                fill="none"
                stroke="hsl(186, 70%, 45%)"
                strokeWidth="3"
                strokeOpacity="0.2"
              />
              <circle
                cx="50"
                cy="50"
                r="45"
                fill="none"
                stroke="url(#turquoiseGradient)"
                strokeWidth="3"
                strokeDasharray="283"
                strokeDashoffset={283 - (283 * progress) / 100}
                strokeLinecap="round"
                className="transition-all duration-300"
                style={{ transformOrigin: 'center', transform: 'rotate(-90deg)' }}
              />
              <defs>
                <linearGradient id="turquoiseGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="hsl(186, 70%, 45%)" />
                  <stop offset="100%" stopColor="hsl(186, 60%, 35%)" />
                </linearGradient>
              </defs>
            </svg>
          </div>

          {/* Inner pulsing circle */}
          <div className="absolute inset-6 bg-gradient-to-br from-turquoise/20 to-maroon/20 rounded-full animate-pulse-slow backdrop-blur-sm" />

          {/* Center percentage */}
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-white font-bold text-lg tabular-nums">
              {Math.round(progress)}%
            </span>
          </div>

          {/* Glow effect */}
          <div className="absolute inset-0 bg-gradient-to-br from-turquoise/30 to-maroon/30 rounded-full blur-xl animate-pulse-glow" />
        </div>

        {/* Loading text with typing animation */}
        <div className="text-center space-y-2">
          <p className="text-white font-bold text-base md:text-lg animate-fade-in-up">
            Janet Marketing Academy
          </p>
          <p className="text-turquoise text-sm md:text-base font-medium animate-fade-in-up-delayed flex items-center justify-center gap-2">
            <span> if your business isn't online....`ni mbaya!!`</span>
            <span className="animate-dots">...</span>
          </p>
        </div>

        {/* Progress bar */}
        <div className="w-64 md:w-80 h-1.5 bg-white/10 rounded-full overflow-hidden backdrop-blur-sm">
          <div 
            className="h-full bg-gradient-to-r from-maroon via-turquoise to-maroon bg-size-200 animate-shimmer rounded-full transition-all duration-300 ease-out"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      <style>{`
        @keyframes logo-entrance {
          0% {
            opacity: 0;
            transform: scale(0.5) translateY(-30px);
          }
          50% {
            transform: scale(1.1);
          }
          100% {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }

        @keyframes spin-slow {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }

        @keyframes spin-reverse {
          0% {
            transform: rotate(360deg);
          }
          100% {
            transform: rotate(0deg);
          }
        }

        @keyframes pulse-slow {
          0%, 100% {
            opacity: 0.4;
            transform: scale(0.95);
          }
          50% {
            opacity: 0.8;
            transform: scale(1.05);
          }
        }

        @keyframes pulse-glow {
          0%, 100% {
            opacity: 0.3;
          }
          50% {
            opacity: 0.6;
          }
        }

        @keyframes fade-in-up {
          0% {
            opacity: 0;
            transform: translateY(10px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes fade-in-up-delayed {
          0%, 30% {
            opacity: 0;
            transform: translateY(10px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes dots {
          0%, 20% {
            content: '.';
          }
          40% {
            content: '..';
          }
          60%, 100% {
            content: '...';
          }
        }

        @keyframes float-slow {
          0%, 100% {
            transform: translate(0, 0) scale(1);
          }
          50% {
            transform: translate(20px, -20px) scale(1.1);
          }
        }

        @keyframes float-slower {
          0%, 100% {
            transform: translate(0, 0) scale(1);
          }
          50% {
            transform: translate(-20px, 20px) scale(1.1);
          }
        }

        @keyframes shimmer {
          0% {
            background-position: 200% center;
          }
          100% {
            background-position: -200% center;
          }
        }

        .animate-logo-entrance {
          animation: logo-entrance 0.8s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .animate-spin-slow {
          animation: spin-slow 3s linear infinite;
        }

        .animate-spin-reverse {
          animation: spin-reverse 2s linear infinite;
        }

        .animate-pulse-slow {
          animation: pulse-slow 2s ease-in-out infinite;
        }

        .animate-pulse-glow {
          animation: pulse-glow 2s ease-in-out infinite;
        }

        .animate-fade-in-up {
          animation: fade-in-up 0.6s cubic-bezier(0.4, 0, 0.2, 1) 0.3s both;
        }

        .animate-fade-in-up-delayed {
          animation: fade-in-up-delayed 0.8s cubic-bezier(0.4, 0, 0.2, 1) both;
        }

        .animate-dots::after {
          content: '...';
          animation: dots 1.5s steps(4, end) infinite;
        }

        .animate-float-slow {
          animation: float-slow 6s ease-in-out infinite;
        }

        .animate-float-slower {
          animation: float-slower 8s ease-in-out infinite;
        }

        .animate-shimmer {
          background-size: 200% 100%;
          animation: shimmer 2s linear infinite;
        }

        .bg-size-200 {
          background-size: 200% 100%;
        }
      `}</style>
    </div>
  );
}